import React from "react";
import { pipe } from "fp-ts/function";
import * as A from "fp-ts/Array";
import * as O from "fp-ts/Option";
import * as R from "fp-ts/Record";

import { BarChart } from "src/components/charts/bar_chart";
import { Separator } from "src/components/separator/separator";
import { Body, PageTitle, SectionTitle } from "src/components/typography/typography";
import { constructTitle } from "src/pages/reports/construct_notes";
import { DomainsCircle } from "src/pages/reports/fsci/all/graphics/domains_circle";
import { isNotNil } from "src/utils/type_guards";

import { meta } from "./meta";

/*
 * Types
 */

export type ExpertiseResourcesProps = {
    readonly reports: Array<any>;
};

/*
 * Main
 */

export const ExpertiseResources = (props: ExpertiseResourcesProps): React.ReactElement => {
    const { reports } = props;

    return (
        <div className="main-report-content-wrap">
            <div className="single-domain-result-top-wrap">
                <DomainsCircle activeDomain="expertiseAndResources" inline />

                <PageTitle>Expertise & Resources</PageTitle>
                <Body>
                    While a sense of purpose is essential for Christian school leaders and teachers, their ability to
                    fulfill that purpose is necessarily dependent upon their expertise as educators. FCSI research shows
                    that flourishing is connected to excellence in educational and school management practices.
                </Body>
                <Body>
                    Constructs related to expertise &amp; resources were significant for five of the seven participant
                    groups surveyed by the FSCI (parents; teachers; leaders/administrators; support staff; and board
                    members).
                </Body>
                <Body>
                    <em>
                        Benchmark data is not included for the purpose of scoring or ranking your school against other
                        schools; rather, it is provided to help your school a) gauge priorities and b) track change over
                        subsequent administrations of the FSCI at your school.
                    </em>
                </Body>
                <Separator />
            </div>

            {pipe(
                R.keys(reports[0].node.results.raw.expertiseAndResources),
                A.map((construct) => (
                    <div className="an-opportunity-item domain-result" key={construct}>
                        {pipe(
                            R.lookup(construct, meta),
                            O.map((m) => (
                                <>
                                    <SectionTitle>{constructTitle(m)}</SectionTitle>
                                    <Body>{m.prologue}</Body>
                                    <BarChart
                                        data={reports
                                            .map((r) => ({
                                                label: r.node.year,
                                                value: r.node.results.raw.expertiseAndResources[construct] * 100,
                                            }))
                                            .concat({
                                                label: "FSCI Benchmark",
                                                value:
                                                    reports[0].node.results.benchmarks.expertiseAndResources[
                                                        construct
                                                    ] * 100,
                                            })}
                                    />
                                    {isNotNil(m.extra) && <Body className="small-domain-text">{m.extra}</Body>}
                                </>
                            )),
                            O.getOrElse(() => <></>),
                        )}
                    </div>
                )),
            )}
        </div>
    );
};
